import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Camera, Trash2, Package, ChevronDown, UserRound, MapPin, Heart, KeyRound } from "lucide-react";
import { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { ShippingForm, validateShipping } from "@/components/ShippingForm";
import { WishlistGrid } from "@/pages/Wishlist";
import { fmtPrice } from "@/data/catalog";
import { api } from "@/lib/api";

const resizeImage = (file, size = 320) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    const s = Math.min(img.width, img.height);
    c.width = c.height = size;
    c.getContext("2d").drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, size, size);
    resolve(c.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

const Avatar = ({ user, onChange }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !file.type.startsWith("image/") || file.size > 5 * 1024 * 1024) return toast.error(t("account.photoErr"));
    setBusy(true);
    try { const avatar = await resizeImage(file); await onChange(avatar); toast.success(t("account.photoSaved")); } catch { toast.error(t("account.photoErr")); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col items-center">
      <button type="button" data-testid="avatar-button" onClick={() => ref.current?.click()} disabled={busy} className="group relative grid h-32 w-32 place-items-center overflow-hidden rounded-full bg-[#c6ff00] ring-4 ring-black">
        {user.avatar ? <img data-testid="avatar-image" src={user.avatar} alt="" className="h-full w-full object-cover" /> : <UserRound size={48} className="text-black" />}
        <span className="absolute inset-0 grid place-items-center bg-black/60 text-[#c6ff00] opacity-0 transition-opacity group-hover:opacity-100"><Camera size={26} /></span>
      </button>
      <input ref={ref} data-testid="avatar-input" type="file" accept="image/*" className="hidden" onChange={pick} />
      <div className="mt-3 flex gap-2">
        <button type="button" data-testid="avatar-change" onClick={() => ref.current?.click()} disabled={busy} className="nv-btn-ghost !px-4 !py-2 text-[11px]"><Camera size={14} /> {t("account.changePhoto")}</button>
        {user.avatar && <button type="button" data-testid="avatar-remove" onClick={() => onChange("")} className="nv-btn-ghost !px-3 !py-2 text-[11px]" aria-label={t("account.removePhoto")}><Trash2 size={14} /></button>}
      </div>
    </div>
  );
};

const OrderRow = ({ o }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const s = o.shipping || {};
  return (
    <li data-testid={`order-${o.id}`} className="rounded-2xl bg-white ring-1 ring-black/5">
      <button type="button" data-testid={`order-toggle-${o.id}`} onClick={() => setOpen(!open)} className="flex w-full items-center gap-4 p-4 text-left">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-black text-[#c6ff00]"><Package size={18} /></span>
        <span className="min-w-0 flex-1">
          <span className="block font-display font-bold">{o.id}</span>
          <span className="block text-xs text-black/50">{new Date(o.date).toLocaleDateString(i18n.language, { day: "2-digit", month: "long", year: "numeric" })} · {o.items.length} {(o.items.length === 1 ? t("cart.item") : t("cart.items"))} · <span className="font-semibold text-[#6f9c00]">{t("account.confirmed")}</span></span>
        </span>
        <span className="font-display text-lg font-bold">{fmtPrice(o.total, i18n.language)}</span>
        <ChevronDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`order-details-${o.id}`} className="border-t border-black/5 p-4">
          <ul className="divide-y divide-black/5">
            {o.items.map((i) => (
              <li key={i.code} className="flex items-center gap-3 py-2 text-sm">
                <img src={i.img} alt="" className="h-12 w-12 rounded-lg bg-[#f6f7f2] object-contain p-1" />
                <span className="flex-1"><span className="block text-[10px] font-bold uppercase text-black/45">{i.brand}</span>{i.name}</span>
                <span className="text-black/60">× {i.qty}</span>
                <span className="w-24 text-right font-semibold">{fmtPrice(i.price * i.qty, i18n.language)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 grid gap-2 text-xs text-black/60 sm:grid-cols-2">
            <p><span className="font-bold uppercase tracking-wider">{t("account.shippedTo")}:</span> {s.firstName} {s.lastName}, {s.address}, {s.zip} {s.city} ({s.country})</p>
            <p className="sm:text-right">{t("cart.shipping")}: {o.shippingCost ? fmtPrice(o.shippingCost, i18n.language) : t("cart.free")} · {t("cart.subtotal")}: {fmtPrice(o.subtotal, i18n.language)}</p>
          </div>
        </div>
      )}
    </li>
  );
};

const PwdField = ({ id, label, value, onChange }) => (
  <div><label htmlFor={id} className="nv-label">{label}</label><input id={id} data-testid={`pwd-${id}`} type="password" autoComplete={id === "currentPassword" ? "current-password" : "new-password"} value={value} onChange={(e) => onChange(id, e.target.value)} className="nv-input" /></div>
);

const PasswordForm = () => {
  const { t } = useTranslation();
  const [f, setF] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const onChange = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (f.newPassword.length < 6) return setErr("errPwd");
    if (f.newPassword !== f.confirm) return setErr("errPwdMatch");
    setBusy(true);
    try {
      await api.put("/auth/password", { currentPassword: f.currentPassword, newPassword: f.newPassword });
      toast.success(t("account.pwdChanged"));
      setF({ currentPassword: "", newPassword: "", confirm: "" });
    } catch (e) {
      const d = apiError(e);
      setErr(["errWrongPassword", "errSamePassword"].includes(d) ? d : "errGeneric");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="password-form" className="rounded-3xl border border-black/10 p-8">
      <h2 className="inline-flex items-center gap-2 font-display text-xl font-bold"><KeyRound size={18} /> {t("account.password")}</h2>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <PwdField id="currentPassword" label={t("account.currentPassword")} value={f.currentPassword} onChange={onChange} />
        <PwdField id="newPassword" label={t("account.newPassword")} value={f.newPassword} onChange={onChange} />
        <PwdField id="confirm" label={t("account.confirmPassword")} value={f.confirm} onChange={onChange} />
      </div>
      {err && <p data-testid="password-error" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{t(`auth.${err}`)}</p>}
      <button data-testid="password-save" disabled={busy} className="nv-btn-dark mt-6">{t("account.changePassword")}</button>
    </form>
  );
};

export default function Account() {
  const { t, i18n } = useTranslation();
  const { user, ready, update, logout } = useAuth();
  const { count: wishCount } = useWishlist();
  const [f, setF] = useState(user);
  const [orders, setOrders] = useState([]);
  const [avatar, setAvatar] = useState(user?.avatar || "");
  useEffect(() => { if (user) { setF(user); setAvatar(user.avatar || ""); } }, [user]);
  useEffect(() => { if (user) api.get("/orders").then((r) => setOrders(r.data)).catch(() => {}); }, [user]);
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!f) return null;

  const save = async (e) => {
    e.preventDefault();
    const v = validateShipping(f);
    if (v) return toast.error(t(`auth.${v}`));
    const r = await update(f);
    if (r.error) return toast.error(t(`auth.${r.error}`));
    toast.success(t("account.saved"));
  };
  const changeAvatar = async (a) => { const r = await api.put("/auth/avatar", { avatar: a }); setAvatar(r.data.avatar || ""); };

  return (
    <main className="nv-container py-12 pb-24">
      <div className="grid gap-8 lg:grid-cols-[320px_1fr]">
        <aside className="space-y-4">
          <div data-testid="account-card" className="nv-grain rounded-3xl bg-black p-8 text-center text-white">
            <Avatar user={{ ...user, avatar }} onChange={changeAvatar} />
            <h1 data-testid="account-title" className="mt-5 font-display text-2xl font-bold">{user.firstName} {user.lastName}</h1>
            <p className="text-sm text-white/60">{user.email}</p>
            <dl className="mt-6 space-y-2 text-left text-xs">
              {user.referrer && <div className="flex justify-between gap-3 rounded-xl bg-white/5 px-3 py-2"><dt className="text-white/50">{t("account.referredBy")}</dt><dd data-testid="account-referrer" className="font-semibold">{user.referrer}</dd></div>}
              {user.createdAt && <div className="flex justify-between gap-3 rounded-xl bg-white/5 px-3 py-2"><dt className="text-white/50">{t("account.memberSince")}</dt><dd className="font-semibold">{new Date(user.createdAt).toLocaleDateString(i18n.language)}</dd></div>}
              <div className="flex justify-between gap-3 rounded-xl bg-white/5 px-3 py-2"><dt className="text-white/50">{t("account.orders")}</dt><dd className="font-semibold">{orders.length}</dd></div>
              <div className="flex justify-between gap-3 rounded-xl bg-white/5 px-3 py-2"><dt className="text-white/50">{t("wishlist.title")}</dt><dd className="font-semibold">{wishCount}</dd></div>
            </dl>
            <button data-testid="account-logout" onClick={logout} className="nv-btn-lime mt-6 w-full !py-3 text-xs">{t("nav.logout")}</button>
          </div>
        </aside>

        <div className="space-y-8">
          <form onSubmit={save} className="rounded-3xl border border-black/10 p-8" data-testid="account-form">
            <h2 className="inline-flex items-center gap-2 font-display text-xl font-bold"><MapPin size={18} /> {t("account.personal")}</h2>
            <div className="mt-6"><ShippingForm form={f} onChange={(k, v) => setF((p) => ({ ...p, [k]: v }))} withReferrer /></div>
            <button data-testid="account-save" className="nv-btn-dark mt-6">{t("account.save")}</button>
          </form>

          <PasswordForm />

          <section className="rounded-3xl bg-[#f4f5f0] p-8">
            <h2 className="inline-flex items-center gap-2 font-display text-xl font-bold"><Package size={18} /> {t("account.orders")} <span className="text-black/40">({orders.length})</span></h2>
            {orders.length === 0 ? <p data-testid="no-orders" className="mt-4 text-sm text-black/60">{t("account.noOrders")}</p> : (
              <ul className="mt-5 space-y-3">{orders.map((o) => <OrderRow key={o.id} o={o} />)}</ul>
            )}
          </section>

          <section data-testid="account-wishlist">
            <h2 className="inline-flex items-center gap-2 font-display text-xl font-bold"><Heart size={18} /> {t("wishlist.title")} <span className="text-black/40">({wishCount})</span></h2>
            <div className="mt-5"><WishlistGrid compact /></div>
          </section>
        </div>
      </div>
    </main>
  );
}
